import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Popover from 'common/components/molecules/Popover';
import MenuSelect from 'common/components/molecules/MenuSelect';
import {
  INK, INK_2, INK_3, LINE, BG, CRITICAL,
  BLUE, BLUE_T, BLUE_LINE,
  RADIUS, POPOVER_SHADOW, THIN_SCROLLBAR
} from 'common/styles/listUI';
import { brandInk, brandLine, brandTint } from 'common/styles/brandTone';

// Activate a role="button" from the keyboard the way a click would — on Enter
// or Space only. Replaces onKeyPress, which fired for every character key
// (so typing while the ✕ was focused removed the chip) and is deprecated.
const onActivateKey = handler => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler();
  }
};

/**
 * Generic applied-filter bar for the redesigned list UI (sc-35741). Renders the
 * "+ Filter" pill with a two-column popover (field list + option panel), the
 * removable applied-filter chips, optional quick-filter presets, optional blue
 * insight chips, and Sort / Group dropdowns.
 *
 * All labels are supplied pre-translated by the caller (see `labels`), so the
 * component stays i18n-agnostic. The option panel for the active field is a
 * render-prop (`renderFieldOptions`) so each page controls its own option UI
 * (checkboxes, radios, search inputs, a custom-date calendar, ...).
 *
 * `testIds` lets a page reproduce an exact selector contract (e.g. the Assets
 * cucumber suite: asset-filter-add-button / asset-filter-field-option).
 */

const DEFAULT_LABELS = {
  addFilter: 'Filter',
  filterBy: 'Filter by',
  filterByPlaceholder: 'Filter by…',
  remove: 'Remove',
  insights: 'Insights',
  clearAll: 'Clear all',
  quick: 'Quick',
  sort: 'Sort',
  group: 'Group'
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 20px;
  background: white;
  border-bottom: 1px solid ${LINE};
`;

const AddWrap = styled.div`
  position: relative;
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px dashed ${props => (props.active ? brandInk(props) : LINE)};
  background: ${props => (props.active ? brandTint(props) : 'transparent')};
  color: ${props => (props.active ? brandInk(props) : INK_2)};
  font-family: inherit;
  font-weight: 600;
  border-radius: ${RADIUS.button};
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  box-sizing: border-box;

  &:hover { border-color: ${brandInk}; color: ${brandInk}; background: ${brandTint}; }
`;

const Pop = styled.div`
  display: flex;
  background: white;
  border: 1px solid ${LINE};
  border-radius: ${RADIUS.menu};
  box-shadow: ${POPOVER_SHADOW};
  overflow: hidden;
`;

const PopCol = styled.div`
  min-width: 172px;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;
  ${THIN_SCROLLBAR}

  & + & { border-left: 1px solid ${LINE}; min-width: 220px; }
`;

const PopTitle = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${INK_3};
  padding: 6px 8px 4px;
`;

const FieldSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${LINE};
  border-radius: 6px;
  padding: 5px 9px;
  margin: 4px 4px 8px;
  color: ${INK_3};

  svg { width: 11px; height: 11px; flex: 0 0 auto; }
  input {
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    width: 100%;
    color: ${INK};
  }
  input::placeholder { color: ${INK_3}; }
`;

const SearchGlyph = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const FieldItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  background: ${props => (props.active ? brandTint(props) : 'transparent')};
  color: ${props => (props.active ? brandInk(props) : INK)};
  font-family: inherit;
  font-size: 12.5px;
  text-align: left;
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;

  &:hover { background: ${props => (props.active ? brandTint(props) : BG)}; }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${LINE};
  background: ${BG};
  border-radius: ${RADIUS.button};
  padding: 4px 10px;
  font-size: 12px;
  color: ${INK};

  .fk { color: ${INK_3}; }
  .fx {
    color: ${INK_3};
    font-size: 13px;
    line-height: 1;
    padding-left: 2px;
    cursor: pointer;
  }
  .fx:hover { color: ${CRITICAL}; }
`;

const ClearAll = styled.button`
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 12px;
  color: ${INK_2};
  cursor: pointer;
  padding: 4px 6px;
  text-decoration: underline;

  &:hover { color: ${CRITICAL}; }
`;

const Right = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Presets = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PresetsLabel = styled.span`
  font-size: 11px;
  color: ${INK_3};
`;

const Preset = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid ${props => (props.active ? brandLine(props) : LINE)};
  background: ${props => (props.active ? brandTint(props) : 'white')};
  color: ${props => (props.active ? brandInk(props) : INK_2)};
  font-weight: ${props => (props.active ? 700 : 400)};
  font-family: inherit;
  border-radius: ${RADIUS.pill};
  padding: 4px 10px;
  font-size: 11.5px;
  cursor: pointer;
  white-space: nowrap;

  &:hover { border-color: ${INK_3}; color: ${props => (props.active ? brandInk(props) : INK)}; }

  .pd { width: 7px; height: 7px; border-radius: 50%; }
`;

const Divider = styled.span`
  width: 1px;
  height: 18px;
  background: ${LINE};
  margin: 0 2px;
`;

const InsightChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${BLUE_LINE};
  background: ${BLUE_T};
  color: ${BLUE};
  border-radius: ${RADIUS.button};
  padding: 4px 10px;
  font-size: 12px;

  .fk { color: ${BLUE}; opacity: 0.8; }
  .fx {
    color: ${BLUE};
    font-size: 13px;
    line-height: 1;
    padding-left: 2px;
    cursor: pointer;
  }
  .fx:hover { color: ${CRITICAL}; }
`;

const InsightBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid ${props => (props.active ? BLUE_LINE : LINE)};
  background: ${props => (props.active ? BLUE_T : 'white')};
  color: ${props => (props.active ? BLUE : INK_2)};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  border-radius: ${RADIUS.button};
  padding: 6px 10px;
  cursor: pointer;
  box-sizing: border-box;

  &:hover { border-color: ${BLUE}; color: ${BLUE}; }
`;

class FilterBar extends React.Component {
  state = { popOpen: false, activeField: null, fieldSearch: '' };

  setAddBtnRef = (node) => { this.addBtnEl = node; };

  getAddBtn = () => this.addBtnEl;

  togglePop = () => {
    this.setState((prev) => {
      const popOpen = !prev.popOpen;
      const { filterFields } = this.props;
      const activeField = popOpen
        ? (prev.activeField || (filterFields[0] && filterFields[0].name))
        : prev.activeField;
      return { popOpen, activeField };
    });
  };

  closePop = () => {
    this.setState({ popOpen: false, fieldSearch: '' });
  };

  selectField = (name) => { this.setState({ activeField: name }); };

  render() {
    const {
      chips, onRemoveChip, onClearAll,
      presets, activePresetId, onTogglePreset,
      filterFields, renderFieldOptions,
      sortOptions, activeSort, onChangeSort,
      groupOptions, activeGroup, onChangeGroup,
      showInsights, insightsOpen, onToggleInsights, insightChips, onClearInsight,
      labels, testIds
    } = this.props;
    const { popOpen, activeField, fieldSearch } = this.state;
    const lbl = { ...DEFAULT_LABELS, ...labels };
    // defaultProps only cover `undefined`, so normalise the list props once
    // here rather than guarding (or forgetting to guard) at each use site.
    const safePresets = presets || [];
    const safeSortOptions = sortOptions || [];
    const safeGroupOptions = groupOptions || [];
    const lcFieldSearch = fieldSearch.toLowerCase();
    const visibleFields = lcFieldSearch
      ? filterFields.filter(f => (f.title || '').toLowerCase().includes(lcFieldSearch))
      : filterFields;

    return (
      <Bar>
        <AddWrap>
          <AddBtn
            type="button"
            innerRef={this.setAddBtnRef}
            active={popOpen}
            data-testid={testIds.addButton}
            onClick={this.togglePop}
          >
            {`+ ${lbl.addFilter}`}
          </AddBtn>
          <Popover open={popOpen} getAnchor={this.getAddBtn} onClose={this.closePop} align="left" gap={6}>
            <Pop>
              <PopCol>
                <PopTitle>{lbl.filterBy}</PopTitle>
                <FieldSearch>
                  <SearchGlyph />
                  <input
                    type="text"
                    placeholder={lbl.filterByPlaceholder}
                    value={fieldSearch}
                    onChange={e => this.setState({ fieldSearch: e.target.value })}
                  />
                </FieldSearch>
                {visibleFields.map(f => (
                  <FieldItem
                    key={f.name}
                    type="button"
                    active={activeField === f.name}
                    data-testid={testIds.fieldOption}
                    data-filter-name={f.name}
                    onClick={() => this.selectField(f.name)}
                  >
                    {f.title}
                  </FieldItem>
                ))}
              </PopCol>
              <PopCol>
                {activeField && renderFieldOptions(activeField)}
              </PopCol>
            </Pop>
          </Popover>
        </AddWrap>

        {chips.map(chip => (
          <Chip key={chip.name}>
            <span className="fk">{`${chip.title}:`}</span>
            {chip.text}
            <span
              className="fx"
              role="button"
              tabIndex={0}
              title={lbl.remove}
              onClick={() => onRemoveChip(chip.name)}
              onKeyDown={onActivateKey(() => onRemoveChip(chip.name))}
            >
              ✕
            </span>
          </Chip>
        ))}
        {insightChips.map(chip => (
          <InsightChip key={chip.id}>
            <span className="fk">{`${lbl.insights}:`}</span>
            {chip.label}
            <span
              className="fx"
              role="button"
              tabIndex={0}
              title={lbl.remove}
              onClick={() => onClearInsight(chip.id)}
              onKeyDown={onActivateKey(() => onClearInsight(chip.id))}
            >
              ✕
            </span>
          </InsightChip>
        ))}
        {chips.length > 0 && (
          <ClearAll type="button" onClick={onClearAll}>
            {lbl.clearAll}
          </ClearAll>
        )}

        <Right>
          {safePresets.length > 0 && (
            <Presets>
              <PresetsLabel>
                {lbl.quick}
                :
              </PresetsLabel>
              {safePresets.map(p => (
                <Preset
                  key={p.id}
                  type="button"
                  active={activePresetId === p.id}
                  onClick={() => onTogglePreset(p.id)}
                >
                  <span className="pd" style={{ background: p.dot }} />
                  {p.label}
                </Preset>
              ))}
            </Presets>
          )}
          {safePresets.length > 0
            && (safeSortOptions.length > 0 || safeGroupOptions.length > 0 || showInsights)
            && <Divider />}
          {safeSortOptions.length > 0 && (
            <MenuSelect
              align="right"
              value={activeSort}
              onChange={onChangeSort}
              prefix={lbl.sort}
              options={safeSortOptions}
            />
          )}
          {safeGroupOptions.length > 0 && (
            <MenuSelect
              align="right"
              value={activeGroup}
              onChange={onChangeGroup}
              prefix={lbl.group}
              options={safeGroupOptions}
            />
          )}
          {showInsights && (
            <InsightBtn type="button" active={insightsOpen} onClick={onToggleInsights}>
              {lbl.insights}
              {insightsOpen ? ' ▴' : ' ▾'}
            </InsightBtn>
          )}
        </Right>
      </Bar>
    );
  }
}

FilterBar.propTypes = {
  // `name` is the chip's identity, not just its React key: it is what gets
  // handed back to onRemoveChip, so it must be present and unique per chip.
  chips: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    title: PropTypes.string,
    text: PropTypes.node
  })),
  onRemoveChip: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  presets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    dot: PropTypes.string,
    label: PropTypes.string
  })),
  activePresetId: PropTypes.string,
  onTogglePreset: PropTypes.func,
  filterFields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string
  })),
  renderFieldOptions: PropTypes.func.isRequired,
  sortOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })),
  activeSort: PropTypes.string,
  onChangeSort: PropTypes.func,
  groupOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })),
  activeGroup: PropTypes.string,
  onChangeGroup: PropTypes.func,
  showInsights: PropTypes.bool,
  insightsOpen: PropTypes.bool,
  onToggleInsights: PropTypes.func,
  insightChips: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string
  })),
  onClearInsight: PropTypes.func,
  labels: PropTypes.object,
  testIds: PropTypes.shape({
    addButton: PropTypes.string,
    fieldOption: PropTypes.string
  })
};

FilterBar.defaultProps = {
  chips: [],
  presets: [],
  activePresetId: null,
  onTogglePreset: () => {},
  filterFields: [],
  sortOptions: [],
  activeSort: '',
  onChangeSort: () => {},
  groupOptions: [],
  activeGroup: '',
  onChangeGroup: () => {},
  showInsights: false,
  insightsOpen: false,
  onToggleInsights: () => {},
  insightChips: [],
  onClearInsight: () => {},
  labels: {},
  testIds: {}
};

export default FilterBar;
